import React, { Suspense, useEffect, useState } from "react";
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import "../src/app.scss";
import ScrollToTop from "./components/ScrollToTop/ScrollToTop";

// Auth Pages
import Login from "./pages/Auth/Login";
import Signup from "./pages/Auth/Signup";
import ForgotPassword from "./pages/Auth/ForgotPassword";
import OTPVerification from "./pages/Auth/OTPVerification";
import ChangePassword from "./pages/Auth/ChangePassword";
import ContactForm from "./pages/Auth/ContactForm";

// Landing + Other Pages
import Home from "./pages/Landing/Home";
import Pricing from "./pages/Pricing/Pricing";
import EnterpriseForm from "./pages/Pricing/EnterpriseForm";

// Dashboard Pages (User Dashboard)
import Dashboard from "./pages/Dashboard/Dashboard";
import Users from "./pages/Users/Users";
import ApiCatalogue from "./pages/ApiCatalogue/ApiCatalogue";
import ApiTryOut from "./pages/ApiTryOut/ApiTryOut";
import ApiKeys from "./pages/APIKeys/ApiKeys";
import Billing from "./pages/Billing/Billing";
import ActivityLogs from "./pages/ActivityLogs/ActivityLogs";

// Admin Pages
import AdminDashboard from "./pages/Admin/AdminDashboard/AdminDashboard";
import AdminUsers from "./pages/Admin/AdminUsers/AdminUsers";
import EditAdminUser from "./pages/Admin/EditAdminUser/EditAdminUser";
import AdminCatalogue from "./pages/Admin/AdminCatalogue/AdminCatalogue";
import CreateApiProduct from "./pages/Admin/AdminCatalogue/CreateApiProduct/CreateApiProduct";
import HealthApi from "./pages/Admin/AdminCatalogue/HealthApi";
import AdminApiUsage from "./pages/Admin/AdminApiUsage/AdminApiUsage";
import ManageApiCredentials from "./pages/Admin/ManageApiCredentials/ManageApiCredentials";


import BillingManagement from "./pages/Admin/BillingManagement/BillingManagement";
import BillingManagementView from "./pages/Admin/BillingManagement/BillingManagementView";


import RolesPermissions from "./pages/Admin/RolesPermissions/RolesPermissions";
import RoleForm from "./pages/Admin/RolesPermissions/RoleForm";
import AuditLog from "./pages/Admin/AuditLog/AuditLog";
import AdminPricing from "./pages/Admin/AdminPricing/AdminPricing";
import AdminProductUsage from "./pages/Admin/AdminProductUsage/AdminProductUsage";
import ProductAccess from "./pages/Admin/ProductAccess/ProductAccess";
import UserProfile from "./pages/UsersProfile/UserProfile";
import Loader from "./components/Loader/Loader";
import ApiCatalogueHome from "./pages/Landing/ApiCatalogueHome";
import AboutUs from "./pages/Landing/AboutUs";
import ProductsPage from "./pages/Landing/ProductsPage";
import PlatformOverview from "./pages/Landing/PlatformOverview";
// import AiBankers from "./pages/Landing/AiBankers"; // TODO: enable when AI Bankers page goes live
import ContactUsPage from "./pages/Landing/ContactUsPage";
import PrivacyPolicy from "./pages/Landing/PrivacyPolicy";
import NotificationTemplate from "./pages/Admin/NotificationTemplate/NotificationTemplate";
import AddNotificationTemplate from "./pages/Admin/NotificationTemplate/AddNotificationTemplate/AddNotificationTemplate";
// import PostLoginGuard from "./routes/PostLoginGuard";
import PageNotFound from "./pages/PageNotFound/PageNotFound";
import GoogleAuthHandler from "./pages/Auth/GoogleAuthHandler";
import MicrosoftAuthHandler from "./pages/Auth/MicrosoftAuthHandler";
// import EditApiProduct from "./pages/Admin/AdminCatalogue/CreateApiProduct/EditApiProduct";
import PaymentFailed from "./pages/Pricing/PaymentFailed";
import PaymentSuccess from "./pages/Pricing/PaymentSuccessModal";
import { isRouteAllowed } from "./utils/routePermission";
import { isLoggedIn } from "./utils/auth"; // NEW
import { loadPermissionsOnce } from "./utils/permissionService";
import Dokgenai from "./pages/products/dokgenai/Dokgenai";
import VerifyX from "./pages/products/verifyx/Verifyx";
import LegalAI from "./pages/products/legalai/Legalai";
import TrackerHeroArt from "./pages/products/livetracker/Livetracker";
import BillingAI from "./pages/products/billingai/Billingai";
import EmailToCase from "./pages/products/emailtocase/Emailtocase";
import VPD from "./pages/products/vpd/Vpd";
import Compliance from "./pages/Compliance/Compliance";
import AdminProducts from "./pages/AdminProducts/AdminProducts";
import SitemapPage from "./pages/Sitemap/Sitemap";
import Organizations from "./pages/Organizations/Organizations";
import AdminEditOrganization from "./pages/Admin/AdminEditOrganization/AdminEditOrganization";
import AdminViewOrganization from "./pages/Admin/AdminViewOrganization/AdminViewOrganization";
import AdminPlans from "./pages/Admin/Plans/AdminPlans";
import PlanView from "./pages/Admin/Plans/PlanView";
import PlanForm from "./pages/Admin/Plans/PlanForm";
import AdminDashboardPricing from "./pages/AdminDashboardPricing/AdminDashboardPricing";
import GlobalSettings from "./pages/Billing/GlobalSettings/GlobalSettings";
import Storage from "./pages/Storage/Storage";
import AdminStorage from "./pages/Admin/AdminStorage/AdminStorage";
import HelpDesk from "./pages/HelpDesk/HelpDesk";
import RaiseTicket from "./pages/HelpDesk/RaiseTicket/RaiseTicket";
import AdminHelpDesk from "./pages/Admin/AdminHelpDesk/AdminHelpDesk";
import HelpDeskConfig from "./pages/Admin/AdminHelpDesk/Config/HelpDeskConfig";
import AdminContactRequests from "./pages/Admin/AdminContactRequests/AdminContactRequests";
import ContactUsConfig from "./pages/Admin/AdminContactRequests/Config/ContactUsConfig";
import AdminAddProduct from "./pages/Admin/AdminAddProduct/AdminAddProduct";
import FeatureUpdates from "./pages/Admin/FeatureUpdates/FeatureUpdates";
import FeatureUpdateForm from "./pages/Admin/FeatureUpdates/FeatureUpdateForm";
import FeatureUpdateView from "./pages/FeatureUpdateView/FeatureUpdateView";

// Lazy Layouts
const MainLayout = React.lazy(() => import("./layouts/MainLayout"));
const PageLayout = React.lazy(() => import("./layouts/PageLayout"));

const DashboardLayout = React.lazy(() => import("./layouts/DashboardLayout"));
const AdminLayout = React.lazy(() => import("./layouts/AdminLayout"));


function RouteGuard() {
    const location = useLocation();
    const [ready, setReady] = useState(false);

    //  permissions bootstrap
    useEffect(() => {
        const init = async () => {
            if (!isLoggedIn()) return;
            await loadPermissionsOnce();
            setReady(true);
        };
        init();
    }, []);

    // 🔹 auth check
    if (!isLoggedIn()) {
        return <Navigate to="/login" replace />;
    }

    // 🔹 permissions loading
    if (!ready) {
        return <Loader />;
    }

    const allowed = isRouteAllowed(location.pathname);
    console.log("RouteGuard - allowed:", allowed);

    if (allowed === false) {
        return <Navigate to="/page-not-found" replace />;
    }

    return <Outlet />;
}

// 🔐 PUBLIC GUARD
function PublicGuard() {
    const loggedIn = isLoggedIn();
    const userType = localStorage.getItem("userType");

    if (loggedIn) {
        // 🔥 admin login
        if (userType === "Internal") {
            return <Navigate to="/admin/dashboard" replace />;
        }

        // 🔥 normal user
        return <Navigate to="/dashboard" replace />;
    }

    return <Outlet />;
}


function App() {
    return (
        <div className="app_wrapper">
            <BrowserRouter>
                <ScrollToTop />
                <Suspense fallback={<Loader />}>
                    <Routes>
                        {/* ================= PUBLIC ================= */}
                        <Route element={<MainLayout />}>

                            <Route path="/" element={<Home />} />
                        </Route>

                        <Route element={<PageLayout />}>
                            <Route path="/api-catalogue-home" element={<ApiCatalogueHome />} />
                            <Route path="/about" element={<AboutUs />} />
                            <Route path="/products" element={<ProductsPage />} />
                            <Route path="/platform-overview" element={<PlatformOverview />} />
                            {/* TODO: enable when AI Bankers page goes live */}
                            {/* <Route path="/ai-bankers" element={<AiBankers />} /> */}
                            <Route path="/products/dokgen-ai" element={<Dokgenai />} />
                            <Route path="/products/verifyx" element={<VerifyX />} />
                            <Route path="/products/legal-ai" element={<LegalAI />} />
                            <Route path="/products/live-tracker" element={<TrackerHeroArt />} />
                            <Route path="/products/billing-ai" element={<BillingAI />} />
                            <Route path="/products/email-to-case" element={<EmailToCase />} />
                            <Route path="/products/vpd-ai" element={<VPD />} />
                            <Route path="/contact" element={<ContactUsPage />} />
                            <Route path="/pricing" element={<Pricing />} />
                            <Route path="/sitemap" element={<SitemapPage />} />
                            <Route path="/privacy-policy" element={<PrivacyPolicy />} />

                            <Route element={<PublicGuard />}>
                                <Route path="/pricing/enterprise-request" element={<EnterpriseForm />} />
                            </Route>

                        </Route>

                        {/* ================= AUTH ================= */}

                        <Route element={<PublicGuard />}>

                            <Route path="/login" element={<Login />} />
                            <Route path="/signup" element={<Signup />} />
                            <Route path="/forgot-password" element={<ForgotPassword />} />
                            <Route path="/otp-verification" element={<OTPVerification />} />
                            <Route path="/change-password" element={<ChangePassword />} />
                            {/* <Route path="/contact-us-form" element={<ContactForm />} /> */}
                            <Route path="/auth/google" element={<GoogleAuthHandler />} />
                            <Route path="/auth/microsoft" element={<MicrosoftAuthHandler />} />

                        </Route>

                        {/* ================= USER DASHBOARD ================= */}
                        <Route element={<DashboardLayout />}>
                            <Route path="/user-profile" element={<UserProfile />} />
                            <Route element={<RouteGuard />}>
                                <Route path="/dashboard" element={<Dashboard />} />
                                <Route path="/users" element={<Users />} />
                                <Route path="/users/edit/:id" element={<EditAdminUser />} />
                                <Route path="/api-catalogue" element={<ApiCatalogue />} />
                                <Route
                                    path="api-catalogue/health-api"
                                    element={<HealthApi />}
                                />
                                <Route path="/billing/pricing" element={<AdminDashboardPricing />} />
                                {/* <Route path="/dashboard/compliance" element={<Compliance />} />
                                <Route path="/dashboard/products" element={<AdminProducts />} /> */}

                                <Route path="/billing/enterprise-request" element={<EnterpriseForm />} />

                                <Route path="/api-catalogue/api-tryout" element={<ApiTryOut />} />
                                <Route path="/api-keys" element={<ApiKeys />} />
                                <Route path="/product-usage" element={<AdminProductUsage />} />
                                <Route path="/billing" element={<Billing />} />
                                <Route path="/storage" element={<Storage />} />
                                <Route path="/help-desk" element={<HelpDesk />} />
                                <Route path="/help-desk/raise" element={<RaiseTicket />} />
                                <Route path="/activity-logs" element={<ActivityLogs />} />
                                <Route path="/billing/payment-success" element={<PaymentSuccess />} />
                                <Route path="/billing/payment-failed" element={<PaymentFailed />} />
                                <Route path="/billing/view/:id" element={<BillingManagementView />} />
                            </Route>
                            <Route path="/compliance" element={<Compliance />} />
                            <Route path="/admin-products" element={<AdminProducts />} />
                            {/* Client-only feature update page (guarded inside the component);
                                opened from the notification sidebar's "View Update" button */}
                            <Route path="/feature-updates/:id" element={<FeatureUpdateView />} />
                        </Route>

                        {/* ================= ADMIN ================= */}
                        <Route path="/admin" element={<AdminLayout />}>
                            <Route element={<RouteGuard />}>
                                <Route path="dashboard" element={<AdminDashboard />} />
                                <Route path="users-management" element={<AdminUsers />} />
                                <Route path="users-management/edit/:id" element={<EditAdminUser />} />

                                <Route path="admin-catalogue" element={<AdminCatalogue />} />
                                <Route
                                    path="admin-catalogue/create-api-product"
                                    element={<CreateApiProduct />}
                                />
                                <Route
                                    path="admin-catalogue/health-api"
                                    element={<HealthApi />}
                                />
                                <Route
                                    path="admin-catalogue/edit/:id"
                                    element={<CreateApiProduct />}
                                />

                                <Route path="api-usage" element={<AdminApiUsage />} />
                                <Route path="product-usage" element={<AdminProductUsage />} />
                                <Route
                                    path="manage-api-credentials"
                                    element={<ManageApiCredentials />}
                                />
                                <Route
                                    path="billing-management"
                                    element={<BillingManagement />}
                                />

                                {/* <Route
  path="billing-management/view"
  element={<BillingManagementView />}
 /> */}
                                <Route
                                    path="/admin/billing-management/view/:id"
                                    element={<BillingManagementView />}
                                />
                                <Route
                                    path="billing-management/global-settings"
                                    element={<GlobalSettings />}
                                />
                                <Route path="storage-management" element={<AdminStorage />} />
                                <Route path="help-desk" element={<AdminHelpDesk />} />
                                <Route path="help-desk/config" element={<HelpDeskConfig />} />

                                <Route path="contact-requests" element={<AdminContactRequests />} />
                                <Route path="contact-requests/config" element={<ContactUsConfig />} />


                                <Route path="role-permission" element={<RolesPermissions />} />
                                <Route path="role-permission/create" element={<RoleForm />} />
                                <Route path="role-permission/edit/:id" element={<RoleForm />} />
                                <Route path="admin-pricing" element={<AdminPricing />} />
                                <Route path="product-access" element={<ProductAccess />} />
                                <Route path="product-access/add-product" element={<AdminAddProduct />} />
                                <Route
                                    path="notification-template"
                                    element={<NotificationTemplate />}
                                />
                                <Route
                                    path="notification-template/add"
                                    element={<AddNotificationTemplate />}
                                />
                                <Route path="audit-logs" element={<AuditLog />} />

                                <Route path="plans" element={<AdminPlans />} />
                                <Route path="plans/view/:id" element={<PlanView />} />
                                <Route path="plans/create" element={<PlanForm />} />
                                <Route path="plans/edit/:id" element={<PlanForm />} />

                                {/* ADMIN API DETAIL */}
                                <Route
                                    path="admin-catalogue/:id"
                                    element={<ApiCatalogue isAdminView={true} />}
                                />
                                <Route
                                    path="admin-catalogue/:id/tryout"
                                    element={<ApiTryOut isAdminView={true} />}
                                />
                                {/* <Route path="dashboard/compliance" element={<Compliance />} />
                                <Route path="dashboard/products" element={<AdminProducts />} /> */}
                                <Route path="organization" element={<Organizations />} />
                                <Route
                                    path="organization/edit/:id"
                                    element={<AdminEditOrganization />}
                                />
                                <Route
                                    path="organization/view/:id"
                                    element={<AdminViewOrganization />}
                                />
                            </Route>
                            <Route path="compliance" element={<Compliance />} />
                            <Route path="admin-products" element={<AdminProducts />} />
                            {/* Super-admin only (guarded inside the component) */}
                            <Route path="product-access/features" element={<FeatureUpdates />} />
                            <Route path="product-access/features/new" element={<FeatureUpdateForm />} />
                            <Route path="product-access/features/:id/edit" element={<FeatureUpdateForm />} />
                            {/* ✅ ADMIN TRY OUT */}
                        </Route>

                        {/* ================= 404 ================= */}
                        <Route path="/page-not-found" element={<PageNotFound />} />
                        <Route path="*" element={<PageNotFound />} />
                    </Routes>
                </Suspense>
            </BrowserRouter>
        </div>
    );
}

export default App;

// const PageNotFound = () => "Page Not Found";

// function App() {
//   return (
//     <BrowserRouter>
//       <Suspense fallback={<Loader />}>
//         <Routes>
//           <Route >
//             <Route path="/api-catalogue-home" element={<ApiCatalogueHome />} />

//           </Route>
//           {/* PUBLIC SITE */}
//           <Route element={<MainLayout />}>
//             <Route path="/" element={<Home />} />
//           </Route>

//           {/* PRICING */}
//           <Route element={<PageLayout />}>
//             <Route path="/pricing" element={<Pricing />} />
//             <Route path="/pricing/enterprise-request" element={<EnterpriseForm />} />
//           </Route>

//           {/* AUTH */}
//           <Route path="/login" element={<Login />} />
//           <Route path="/signup" element={<Signup />} />
//           <Route path="/forgot-password" element={<ForgotPassword />} />
//           <Route path="/otp-verification" element={<OTPVerification />} />
//           <Route path="/change-password" element={<ChangePassword />} />
//           <Route path="/contact-us-form" element={<ContactForm />} />
//           <Route path="/auth/google" element={<GoogleAuthHandler />} />
//           <Route path="/auth/microsoft" element={<MicrosoftAuthHandler />} />
//           {/* Social Login Handling */}

//           {/* USER DASHBOARD */}
//           {/* <Route element={<DashboardLayout />}>
//             <Route path="/dashboard" element={<Dashboard />} />
//             <Route path="/users" element={<Users />} />
//             <Route path="/api-catalogue" element={<ApiCatalogue />} />
//             <Route path="/billing/pricing" element={<Pricing />} />
//             <Route path="/api-tryout" element={<ApiTryOut />} />
//             <Route path="/api-keys" element={<ApiKeys />} />
//             <Route path="/billing" element={<Billing />} />
//             <Route path="/activity-logs" element={<ActivityLogs />} />
//             <Route path="/user-profile" element={<UserProfile />} />
//           </Route> */}
//             <Route element={<DashboardLayout />}>
//               <Route path="/dashboard" element={<Dashboard />} />
//               <Route path="/users" element={<Users />} />
//               <Route path="/api-catalogue" element={<ApiCatalogue />} />
//               <Route path="/billing/pricing" element={<Pricing />} />

//               <Route path="/api-tryout" element={<ApiTryOut />} />
//               <Route path="/api-keys" element={<ApiKeys />} />
//               <Route path="/billing" element={<Billing />} />
//               <Route path="/activity-logs" element={<ActivityLogs />} />
//               <Route path="/user-profile" element={<UserProfile />} />
//             </Route>

//           {/* ADMIN DASHBOARD (NEW) */}
//           {/* <Route path="/admin" element={<AdminLayout />}>
//             <Route path="dashboard" element={<AdminDashboard />} />
//             <Route path="users-management" element={<AdminUsers />} />
//             <Route path="admin-catalogue" element={<AdminCatalogue />} />
//             <Route path="admin-catalogue/create-api-product" element={<CreateApiProduct />} />
//             <Route path="api-usage" element={<AdminApiKeys />} />
//             <Route path="manage-api-credentials" element={<ManageApiCredentials />} />
//             <Route path="billing-management" element={<BillingManagement />} />
//             <Route path="role-permission" element={<RolesPermissions />} />
//             <Route path="audit-logs" element={<AuditLog />} />
//             <Route path="admin-pricing" element={<AdminPricing />} />
//             <Route path="notification-template" element={<NotificationTemplate />} />

//           </Route> */}

//             <Route path="/admin" element={<AdminLayout />}>
//               <Route path="dashboard" element={<AdminDashboard />} />
//               <Route path="users-management" element={<AdminUsers />} />
//               <Route path="admin-catalogue" element={<AdminCatalogue />} />
//               <Route path="admin-catalogue/create-api-product" element={<CreateApiProduct />} />

//               <Route path="api-usage" element={<AdminApiKeys />} />
//               <Route path="manage-api-credentials" element={<ManageApiCredentials />} />
//               <Route path="billing-management" element={<BillingManagement />} />

//               <Route path="role-permission" element={<RolesPermissions />} />
//               <Route path="admin-pricing" element={<AdminPricing />} />
//               <Route path="notification-template" element={<NotificationTemplate />} />

//               <Route path="audit-logs" element={<AuditLog />} />
//               <Route path="admin-catalogue/edit/:id" element={<CreateApiProduct/>}/>

//                 {/* ✅ ADMIN API DETAIL VIEW */}
//   <Route
//     path="api-catalogue/:id"
//     element={<ApiCatalogue isAdminView={true} />}
//   />
//             </Route>

//           {/* NOT FOUND */}
//           <Route path="/page-not-found" element={<PageNotFound />} />

//         </Routes>
//       </Suspense>
//     </BrowserRouter>
//   );
// }
